import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
  Label,
  Rectangle,
  XAxis,
  usePlotArea,
  BarRectangleItem,
  LabelProps as RechartsLabelProps
} from 'recharts';
import { BsBarChartLine } from 'react-icons/bs';
import { ChartContainer } from '@Components/ChartContainer';
import { Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { InfoTooltip } from '@Components/InfoTooltip';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Neutral1000, Neutral160, Neutral400 } from '@src/utils/colors';
import { CreativeInsightsData, RawDataType, Selection } from './creative-insights-constants';
import { RawDataDownloadButton } from './RawDataDownloadButton';
import {
  AIRINGS_BAR_COLORS,
  BETA_BAR_SIZE,
  BETA_CHART_MARGIN,
  CHART_MARGIN,
  CHART_SYNC_ID,
  QUANTITY_BAR_SIZE,
  QUANTITY_VIEW_OPTIONS,
  QUANTITY_VIEW_TOGGLE_WIDTH,
  QuantityView,
  QuantityViewType
} from './tags-chart-constants';
import {
  buildTagBarData,
  formatCurrency,
  getBetaBarWidth,
  getLabelFontSize,
  TagBarDatum
} from './tags-chart-utils';
import { SelectionActions } from './use-creative-insights';
import './TagsChart.scss';

interface TagsChartProps {
  data: CreativeInsightsData;
  selection: Selection;
  actions: SelectionActions;
  kpi: string;
}

export const TagsChart = ({
  data: {
    attributeImportanceData,
    tagImportanceData,
    creativeInfoList,
    attributeTypeBinomial,
    suppressedAttributes,
    fillColors
  },
  selection: { attribute: selectedAttribute },
  actions: { selectAttribute, clearAttribute, toggleTag },
  kpi
}: TagsChartProps) => {
  const tagBarData = useMemo(
    () =>
      buildTagBarData(
        tagImportanceData[selectedAttribute] ?? [],
        selectedAttribute,
        creativeInfoList,
        attributeTypeBinomial
      ),
    [attributeTypeBinomial, creativeInfoList, selectedAttribute, tagImportanceData]
  );

  const dropDownOptions = useMemo(
    () =>
      attributeImportanceData
        .filter(attribute => !suppressedAttributes.has(attribute.name))
        .map(attribute => attribute.name),
    [attributeImportanceData, suppressedAttributes]
  );

  const handleTagsChartClick = useCallback(
    (clickedBar: BarRectangleItem) => toggleTag(String(clickedBar.name ?? '')),
    [toggleTag]
  );

  const [quantityView, setQuantityView] = useState<QuantityViewType>(QuantityView.AIRINGS);

  return (
    <ChartContainer
      className='tags-chart-container'
      title={
        <>
          Tags
          <InfoTooltip size={Size.MD}>
            {`All tactics are baselined against the average effect across a particular attribute's
tactics, which is leveled at zero. The magnitude attached to a given tactic can be
interpreted as the percentage difference in KPI response to be expected from adopting that
tactic as compared to the average effect of the tactic.`}
          </InfoTooltip>
        </>
      }
      rightActions={
        <>
          <Dropdown
            stretch={StretchMode.FIT}
            value={selectedAttribute}
            options={dropDownOptions}
            onChange={selectAttribute}
            clearable
            onClear={clearAttribute}
            label='Attribute'
            placeholder='Select Attribute'
          />
          {selectedAttribute && (
            <RawDataDownloadButton
              dataType={RawDataType.TAG}
              kpi={kpi}
              fileName='linear_creative_betas'
              nameColumn='Tag'
              valueColumn='Beta'
              subject='tag betas'
            />
          )}
        </>
      }
    >
      <div className='tags-container'>
        <TagsBody
          selectedAttribute={selectedAttribute}
          data={tagBarData}
          tagFillColor={fillColors.get(selectedAttribute) ?? ''}
          onBarClick={handleTagsChartClick}
          quantityView={quantityView}
          setQuantityView={setQuantityView}
        />
      </div>
    </ChartContainer>
  );
};

interface TagsBodyProps {
  selectedAttribute: string;
  data: TagBarDatum[];
  tagFillColor: string;
  onBarClick: (clickedBar: BarRectangleItem) => void;
  quantityView: QuantityViewType;
  setQuantityView: Dispatch<SetStateAction<QuantityViewType>>;
}

const TagsBody = ({
  selectedAttribute,
  data,
  tagFillColor,
  onBarClick,
  quantityView,
  setQuantityView
}: TagsBodyProps) => {
  if (!selectedAttribute) {
    return (
      <EmptyState
        className='tags-placeholder'
        icon={<BsBarChartLine size={100} />}
        description='Select an attribute to view tactics for that attribute.'
      />
    );
  }

  return (
    <div className='tags-content'>
      <div className='tags-chart'>
        <BetaChart data={data} tagFillColor={tagFillColor} onBarClick={onBarClick} />
        <QuantityChart data={data} view={quantityView} setView={setQuantityView} />
      </div>
    </div>
  );
};

interface BetaChartProps {
  data: TagBarDatum[];
  tagFillColor: string;
  onBarClick: (clickedBar: BarRectangleItem) => void;
}

const BetaChart = ({ data, tagFillColor, onBarClick }: BetaChartProps) => (
  <div className='beta-chart' data-testid='beta-chart'>
    <ResponsiveContainer initialDimension={{ width: 1, height: 1 }}>
      <BarChart
        data={data}
        margin={BETA_CHART_MARGIN}
        barSize={BETA_BAR_SIZE}
        syncId={CHART_SYNC_ID}
      >
        <XAxis dataKey='name' axisLine={false} tick={false} />
        <YAxis
          tickFormatter={tick => `${tick}%`}
          domain={[
            (dataMin: number) => Math.min(0, dataMin - 30),
            (dataMax: number) => Math.max(0, dataMax + 30)
          ]}
        />
        <Tooltip content={<TagsTooltip />} cursor={<CustomCursor />} />
        <ReferenceLine y={0} stroke={Neutral1000}>
          <Label content={<ReferenceLineLabel />} />
        </ReferenceLine>
        <Bar
          dataKey='val'
          fill={tagFillColor}
          onClick={onBarClick}
          isAnimationActive={false}
          background={{ fill: 'transparent', cursor: 'pointer' }}
          cursor='pointer'
        >
          <LabelList content={<CustomizedLabel />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

interface QuantityChartProps {
  data: TagBarDatum[];
  view: QuantityViewType;
  setView: Dispatch<SetStateAction<QuantityViewType>>;
}

const QuantityChart = ({ data, view, setView }: QuantityChartProps) => {
  const maxQuantity = Math.max(0, ...data.map(tag => tag.quantity));

  return (
    <div className='quantity-chart-wrapper'>
      <ResponsiveContainer initialDimension={{ width: 1, height: 1 }}>
        <BarChart
          data={data}
          margin={CHART_MARGIN}
          barSize={QUANTITY_BAR_SIZE}
          syncId={CHART_SYNC_ID}
        >
          <YAxis
            domain={[0, 'dataMax']}
            axisLine={false}
            tickLine={false}
            tick={false}
            label={<QuantityViewToggle view={view} setView={setView} />}
          />
          <XAxis dataKey='name' hide />
          <Bar
            dataKey='quantity'
            isAnimationActive={false}
            shape={
              view === QuantityView.AIRINGS ? (
                <AiringsBar maxQuantity={maxQuantity} />
              ) : (
                <AggregateSpend categoryCount={data.length} />
              )
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface CustomCursorProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const CustomCursor = ({ x = 0, y = 0, width = 0, height = 0 }: CustomCursorProps) => (
  <Rectangle
    fill={Neutral160}
    x={x}
    y={y}
    width={width}
    height={height * 1.5}
    pointerEvents='none'
  />
);

const ReferenceLineLabel = ({ viewBox }: RechartsLabelProps) => {
  let yVal = 0;
  if (viewBox && 'y' in viewBox && typeof viewBox.y === 'number') {
    yVal = viewBox.y;
  }
  return (
    <text className='reference-line-label' y={yVal - 18}>
      <tspan x={38} dy='1.2em'>
        Baseline
      </tspan>
      <tspan x={38} dy='1.2em'>
        Performance
      </tspan>
    </text>
  );
};

interface CustomizedLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
}

const CustomizedLabel = ({ x = 0, y = 0, width = 0, value = 0 }: CustomizedLabelProps) => (
  <text
    x={x + width / 2}
    y={value >= 0 ? y - 10 : y + 20}
    textAnchor='middle'
    fontSize={getLabelFontSize(width)}
  >
    {`${value}%`}
  </text>
);

interface QuantityViewToggleProps {
  viewBox?: { x?: number; width?: number };
  view: QuantityViewType;
  setView: Dispatch<SetStateAction<QuantityViewType>>;
}

// Right-aligned to the y-axis edge so the toggle sits left of the axis.
const QuantityViewToggle = ({ viewBox, view, setView }: QuantityViewToggleProps) => {
  const x = viewBox?.x ?? 0;
  const width = viewBox?.width ?? 0;
  return (
    <foreignObject
      x={x + width - QUANTITY_VIEW_TOGGLE_WIDTH}
      y={0}
      width={QUANTITY_VIEW_TOGGLE_WIDTH}
      height={32}
    >
      <div className='quantity-view-toggle'>
        <SegmentedControlButton
          size={Size.SM}
          options={QUANTITY_VIEW_OPTIONS}
          selectedOption={view}
          onChange={setView}
        />
      </div>
    </foreignObject>
  );
};

interface AiringsBarProps {
  x?: number;
  width?: number;
  quantity?: number;
  maxQuantity: number;
}

const AiringsBar = ({ x = 0, width = 0, quantity = 0, maxQuantity }: AiringsBarProps) => {
  const filledSegments = maxQuantity
    ? Math.round((quantity / maxQuantity) * AIRINGS_BAR_COLORS.length)
    : 0;
  const segmentCount = AIRINGS_BAR_COLORS.length;

  // Segments stack bottom-up with a 2px gap between them.
  return (
    <g>
      {AIRINGS_BAR_COLORS.map((color, i) => (
        <Rectangle
          key={color}
          fill={i < filledSegments ? color : Neutral400}
          x={x}
          y={4 * (segmentCount - i) - 2}
          width={width}
          height={2}
        />
      ))}
    </g>
  );
};

interface TagsTooltipProps {
  label?: string | number;
}

const TagsTooltip = ({ label }: TagsTooltipProps) => {
  if (!label) {
    return null;
  }
  return <div className='tags-chart-tooltip'>{`Tactic: ${label}`}</div>;
};

interface AggregateSpendProps {
  x?: number;
  width?: number;
  spend?: number;
  categoryCount: number;
}

// Sized to match the beta chart's labels; both charts share a plot width.
const AggregateSpend = ({ x = 0, width = 0, spend = 0, categoryCount }: AggregateSpendProps) => {
  const plotArea = usePlotArea();
  const xVal = x + width / 2;
  const betaBarWidth = getBetaBarWidth(plotArea?.width ?? 0, categoryCount);
  return (
    <text
      x={xVal}
      y={25}
      textAnchor='middle'
      fontSize={getLabelFontSize(betaBarWidth)}
      transform={`rotate(45, ${xVal}, 25)`}
    >
      {formatCurrency(Math.round(spend))}
    </text>
  );
};
